import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { CascadedShadowGenerator } from '@babylonjs/core/Lights/Shadows/cascadedShadowGenerator';
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import '@babylonjs/core/Rendering/depthRendererSceneComponent';
import { DracoDecoder } from '@babylonjs/core/Meshes/Compression/dracoDecoder';
import { BASE } from './config';

export interface GameEngine {
  engine: Engine;
  scene: Scene;
  sun: DirectionalLight;
  shadows: CascadedShadowGenerator;
}

export function createEngine(canvas: HTMLCanvasElement): GameEngine {
  // Draco decoder served from our own origin, no CDN.
  DracoDecoder.DefaultConfiguration = {
    wasmUrl: `${BASE}draco/draco_wasm_wrapper_gltf.js`,
    wasmBinaryUrl: `${BASE}draco/draco_decoder_gltf.wasm`,
    fallbackUrl: `${BASE}draco/draco_decoder_gltf.js`,
  };

  const engine = new Engine(canvas, true, { antialias: true, adaptToDeviceRatio: false, powerPreference: 'high-performance', stencil: false }, false);
  engine.setHardwareScalingLevel(1 / Math.min(window.devicePixelRatio || 1, 2));
  const scene = new Scene(engine);
  scene.clearColor = new Color4(0.56, 0.78, 0.95, 1);
  scene.ambientColor = new Color3(0.35, 0.35, 0.4);
  scene.fogMode = Scene.FOGMODE_EXP2;
  scene.fogDensity = 0.0022;
  scene.fogColor = new Color3(0.72, 0.84, 0.95);
  scene.skipPointerMovePicking = true;
  scene.autoClear = true;
  scene.autoClearDepthAndStencil = true;

  const hemi = new HemisphericLight('hemi', new Vector3(0.2, 1, 0.1), scene);
  hemi.intensity = 0.55;
  hemi.groundColor = new Color3(0.35, 0.33, 0.3);

  const sun = new DirectionalLight('sun', new Vector3(-0.45, -1, 0.35).normalize(), scene);
  sun.intensity = 1.9;
  sun.position = new Vector3(60, 120, -60);

  const shadows = new CascadedShadowGenerator(1024, sun);
  shadows.numCascades = 2;
  shadows.lambda = 0.85;
  shadows.shadowMaxZ = 220;
  shadows.stabilizeCascades = true;
  shadows.filteringQuality = CascadedShadowGenerator.QUALITY_MEDIUM;
  shadows.bias = 0.006;
  shadows.normalBias = 0.05;
  shadows.cascadeBlendPercentage = 0.1;
  shadows.autoCalcDepthBounds = false;
  shadows.depthClamp = true;

  // keep the render buffer matched to the canvas: layout can settle after the engine is created, and a
  // window can move between displays with different pixel ratios
  const fit = () => {
    engine.setHardwareScalingLevel(1 / Math.min(window.devicePixelRatio || 1, 2));
    engine.resize(true);
  };
  window.addEventListener('resize', fit);
  window.addEventListener('orientationchange', () => setTimeout(fit, 100));
  document.addEventListener('fullscreenchange', () => setTimeout(fit, 100));
  document.addEventListener('webkitfullscreenchange', () => setTimeout(fit, 100));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) setTimeout(fit, 50); });
  if ('ResizeObserver' in window) new ResizeObserver(() => fit()).observe(canvas);
  const watchDpr = () => { const mq = matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`); mq.addEventListener('change', () => { fit(); watchDpr(); }, { once: true }); };
  watchDpr();
  setTimeout(fit, 0); setTimeout(fit, 500); setTimeout(fit, 2000);
  return { engine, scene, sun, shadows };
}
